import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Building2 } from 'lucide-react';
import { LOGIN, REGISTER } from '../constants/testIds';

function formatApiError(detail) {
  if (!detail) return 'Ocorreu um erro. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ?? JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    imobiliaria: '',
  });

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let userData;
      if (isRegister) {
        await register(form.name, form.email, form.password, form.imobiliaria);
        toast.success('Conta criada! Bem-vindo ao TesIQ.');
      } else {
        userData = await login(form.email, form.password);
        toast.success('Login realizado com sucesso!');
      }
      navigate(userData?.role === 'super_admin' ? '/admin' : '/');
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#064E3B] flex-col justify-between p-12 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-32 -left-32 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute bottom-20 -right-20 w-64 h-64 rounded-full bg-white/5" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Building2 size={22} className="text-white" />
            </div>
            <span className="text-white text-xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              TesIQ
            </span>
          </div>
          <h1
            className="text-4xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Gestão imobiliária inteligente
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-md">
            Calcule investimentos, gerencie imóveis e leads — tudo em um só lugar, com a precisão que seu cliente merece.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4">
          {[
            { label: 'Corretores', value: '2.400+' },
            { label: 'Simulações', value: '180k+' },
            { label: 'Negócios fechados', value: 'R$ 4bi+' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-2xl font-bold text-white">{s.value}</p>
              <p className="text-white/60 text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Building2 size={24} className="text-[#064E3B]" />
            <span className="text-xl font-bold text-[#064E3B]" style={{ fontFamily: 'Playfair Display, serif' }}>
              TesIQ
            </span>
          </div>

          <div className="mb-8">
            <h2
              className="text-3xl font-bold text-stone-900 mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {isRegister ? 'Criar conta' : 'Entrar'}
            </h2>
            <p className="text-stone-500 text-sm">
              {isRegister
                ? 'Preencha os dados para criar sua conta gratuita'
                : 'Bem-vindo de volta ao TesIQ'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-1.5">
                <Label htmlFor="name" className="text-sm font-semibold text-stone-700">
                  Nome completo
                </Label>
                <Input
                  id="name"
                  data-testid={REGISTER.nameInput}
                  type="text"
                  placeholder="Seu nome"
                  value={form.name}
                  onChange={set('name')}
                  required
                  className="h-11 border-stone-200 focus:border-[#064E3B] focus:ring-[#064E3B]/20"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-semibold text-stone-700">
                E-mail
              </Label>
              <Input
                id="email"
                data-testid={isRegister ? REGISTER.emailInput : LOGIN.emailInput}
                type="email"
                autoComplete="username"
                placeholder="seu@email.com"
                value={form.email}
                onChange={set('email')}
                required
                className="h-11 border-stone-200 focus:border-[#064E3B] focus:ring-[#064E3B]/20"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-semibold text-stone-700">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid={isRegister ? REGISTER.passwordInput : LOGIN.passwordInput}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isRegister ? 'new-password' : 'current-password'}
                  placeholder={isRegister ? 'Mínimo 8 caracteres' : '••••••••'}
                  value={form.password}
                  onChange={set('password')}
                  required
                  className="h-11 pr-10 border-stone-200 focus:border-[#064E3B] focus:ring-[#064E3B]/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {isRegister && (
              <div className="space-y-1.5">
                <Label htmlFor="imobiliaria" className="text-sm font-semibold text-stone-700">
                  Imobiliária (opcional)
                </Label>
                <Input
                  id="imobiliaria"
                  type="text"
                  placeholder="Nome da sua imobiliária"
                  value={form.imobiliaria}
                  onChange={set('imobiliaria')}
                  className="h-11 border-stone-200 focus:border-[#064E3B] focus:ring-[#064E3B]/20"
                />
              </div>
            )}

            {!isRegister && (
              <div className="text-right">
                <button
                  type="button"
                  data-testid={LOGIN.forgotPasswordLink}
                  className="text-sm text-[#064E3B] hover:underline font-medium"
                  onClick={() => toast.info('Em breve: redefinição de senha por e-mail')}
                >
                  Esqueceu a senha?
                </button>
              </div>
            )}

            <Button
              type="submit"
              data-testid={isRegister ? REGISTER.submitButton : LOGIN.submitButton}
              disabled={loading}
              className="w-full h-11 bg-[#064E3B] hover:bg-[#022C22] text-white font-semibold rounded-lg transition-all active:scale-[0.98]"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : isRegister ? (
                'Criar conta grátis'
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-stone-500 mt-6">
            {isRegister ? 'Já tem uma conta?' : 'Não tem uma conta?'}{' '}
            <button
              data-testid={isRegister ? REGISTER.loginLink : LOGIN.registerLink}
              onClick={() => setIsRegister((v) => !v)}
              className="text-[#064E3B] font-semibold hover:underline"
            >
              {isRegister ? 'Entrar' : 'Criar conta grátis'}
            </button>
          </p>

          <p className="text-center text-xs text-stone-400 mt-8">
            Demo: corretor@demo.com / Demo@TesIQ2025
          </p>
        </div>
      </div>
    </div>
  );
}
